/**
 * Held Funds tab for the Customer Detail modal.
 *
 * Lets the user freeze a portion of a customer's credit balance so the
 * till refuses to spend it on unrelated purchases. Typical use case: a
 * customer prepays for stock we're ordering in for them — the money
 * lands as a credit journal on their account, then a reservation is
 * added to keep those rands earmarked until the ordered stock arrives.
 *
 * The reservation is metadata only: the stored account_balance is
 * untouched, so every historical balance report stays identical. It's
 * the `available_credit` value returned by the customer endpoints that
 * gets reduced — that's what the checkout modal reads at till time.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Lock, Unlock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HeldFundsTab = ({ customerId, onReservationChanged }) => {
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [totalActive, setTotalActive] = useState(0);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);
  const [showReleased, setShowReleased] = useState(false);

  const load = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/customers/${customerId}/reservations?include_released=${showReleased}`
      );
      setReservations(r.data?.reservations || []);
      setTotalActive(r.data?.total_active || 0);
    } catch (err) {
      toast.error("Failed to load reservations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId, showReleased]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than R0"); return; }
    if (!description.trim() || description.trim().length < 3) {
      toast.error("Enter a short reason so anyone reading the ledger knows why the money is held");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/customers/${customerId}/reservations`, {
        amount: amt,
        description: description.trim(),
        reference: reference.trim() || undefined,
      });
      toast.success(`Held aside R${amt.toFixed(2)}`);
      setAmount(""); setDescription(""); setReference("");
      await load();
      onReservationChanged?.();
    } catch (err) {
      const raw = err.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : err.message || "Could not save reservation");
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async (r) => {
    const reason = window.prompt(
      `Release R${(r.amount || 0).toFixed(2)} back into ${r.customer_name || "the customer"}'s available credit?\n\nReason (optional):`,
      ""
    );
    if (reason === null) return; // user pressed Cancel
    try {
      await axios.post(
        `${API}/customers/${customerId}/reservations/${r.id}/release`,
        { reason: reason || null }
      );
      toast.success("Reservation released — money is spendable again");
      await load();
      onReservationChanged?.();
    } catch (err) {
      const raw = err.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : err.message || "Release failed");
    }
  };

  const fmt = (v) => `R ${(Number(v) || 0).toFixed(2)}`;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="held-funds-tab">
      {/* Header banner */}
      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-amber-900">
            Hold aside {fmt(totalActive)} of this customer's credit
          </div>
          <div className="text-amber-800/80 text-xs mt-1 max-w-2xl">
            Money you freeze here stays on the customer's ledger but is <b>not</b> available
            at the till. Use it when a customer has prepaid for stock we're ordering in for
            them — the reservation keeps their credit earmarked until the ordered stock
            arrives and gets rung up.
          </div>
        </div>
      </div>

      {/* New reservation form */}
      <form
        onSubmit={handleCreate}
        className="border rounded-lg p-4 bg-white"
        data-testid="held-funds-create-form"
      >
        <div className="font-semibold mb-3 text-gray-900 flex items-center gap-2">
          <Lock className="h-4 w-4 text-gray-500" />
          Freeze a new amount
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Amount (R)</Label>
            <Input
              type="number" min="0.01" step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="held-funds-amount-input"
            />
          </div>
          <div>
            <Label className="text-xs">Reference (optional)</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. PO-1234 / invoice #"
              data-testid="held-funds-ref-input"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs">Reason for holding this amount</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="e.g. Deposit for pallet of Epol lucern feed on order — PO-1234"
              data-testid="held-funds-desc-input"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            type="submit"
            disabled={saving || !amount || !description.trim()}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="held-funds-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />}
            Hold aside
          </Button>
        </div>
      </form>

      {/* Reservation list */}
      <div className="border rounded-lg overflow-hidden" data-testid="held-funds-list">
        <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b">
          <div className="text-sm font-semibold text-gray-700">
            {showReleased ? "All reservations" : "Active reservations"}
          </div>
          <label className="text-xs text-gray-600 flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={showReleased}
              onChange={(e) => setShowReleased(e.target.checked)}
              data-testid="held-funds-show-released"
            />
            Show released
          </label>
        </div>
        {loading ? (
          <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
        ) : reservations.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            {showReleased ? "No reservations recorded." : "No money is being held aside for this customer."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-left">Ref</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => {
                const active = r.status === "active";
                return (
                  <tr
                    key={r.id}
                    className={active ? "border-t" : "border-t bg-gray-50 text-gray-500"}
                    data-testid={`held-funds-row-${r.id}`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${active ? "text-amber-700 font-semibold" : "line-through"}`}>
                      {fmt(r.amount)}
                    </td>
                    <td className="px-3 py-2 max-w-md">{r.description || "—"}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{r.reference || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      {active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                          <Lock className="h-3 w-3" /> HELD
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                          <Unlock className="h-3 w-3" /> released
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {active ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRelease(r)}
                          data-testid={`held-funds-release-${r.id}`}
                        >
                          <Unlock className="h-3 w-3 mr-1" />
                          Release
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400" title={r.release_reason || ""}>
                          {r.released_at ? fmtDate(r.released_at) : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default HeldFundsTab;
